/**
 * liveVideo.js
 *
 * @description Contains code to interact with the live video API.
 *
 * @author Moshe Berman
 * @copyright 2018, Owal Inc.
 *
 * Created on 02/14/2018.
 */

import { owalAPIClient } from './apiClient'

const LIVE_VIDEO_SERVER_URL = 'https://api.owal.io'

/**
 * Tracks any live heartbeats.
 */
let heartbeats = {}

/**
 * Ask the server to start an ffmpeg process and return information about how to access the HLS stream.
 *
 * @param {any} cameraUUID The UUID of the camera to livestream from.
 * @param {any} duration How long to keep the ffmpeg process alive, in seconds. (The default is 600 seconds, or five minutes.)
 */
export function startStreamForCameraUUID (cameraUUID, duration = 600) {
  /** An object used to access live video. */
  const liveVideo = owalAPIClient(LIVE_VIDEO_SERVER_URL)
  const params = {
    camera_uuid: cameraUUID,
    duration,
  }
  return liveVideo.post('/api/v1/hlsvideo/', params)
}

/**
 * Checks the stream state for all cameras, or a specific camera.
 *
 * @export
 * @param {String} cameraUUID The UUID of the camera to check. Optional. Omitting will return all cameras in an array.
 */
export function checkStreamState (cameraUUID) {
  /** An object used to access live video. */
  const liveVideo = owalAPIClient(LIVE_VIDEO_SERVER_URL)
  return liveVideo.get(`/api/v1/hlsvideo/${cameraUUID || ''}`)
}

/**
 * Post a heartbeat to the server.
 *
 * @export
 * @param {String} cameraUUID The UUID of the camera to livestream from.
 * @param {Number} [durationInSeconds=570] The new duration for stream to be kept alive. (May be less than the current time_to_live.)
 */
export function putHeartbeat (cameraUUID, durationInSeconds = 570, retries = 3) {
  /** An object used to access live video. */
  const liveVideo = owalAPIClient(LIVE_VIDEO_SERVER_URL)
  const params = {
    camera_uuid: cameraUUID,
    durationInSeconds,
  }

  // console.debug(`Heartbeat for ${cameraUUID}.`)
  heartbeats[cameraUUID] = setTimeout(() => {
    liveVideo.put(`/api/v1/hlsvideo/${cameraUUID}`, params).then(res => res.data).then(data => {
      putHeartbeat(cameraUUID, durationInSeconds)
      // console.log(`Next heartbeat in ${durationInSeconds} seconds.`)
      // console.log(`Successful heartbeat.`)
      // console.log(data)
    }).catch(error => {
      console.error(`Heartbeat failed, trying again in 3 seconds.`, error)
      putHeartbeat(cameraUUID, 3, retries - 1)
    })
  }, durationInSeconds * 1000)
}

/**
 * Cancels the heartbeat for a specific camera. If no camera is specified, clears all.
 *
 * @param {any} cameraUUID The camera whose heartbeat to kill.
 */
export function stopHeartbeat (cameraUUID) {
  if (!cameraUUID) {
    for (const cameraId in heartbeats) {
      stopHeartbeat(cameraId)
    }
    return
  }
  const nextBeat = heartbeats[cameraUUID]
  clearTimeout(nextBeat)
  heartbeats = {}
  // console.debug(`Cleared heartbeat for ${cameraUUID}.`)
  // console.debug(heartbeats)
}

/**
 * Stops the stream for a specified camera.
 *
 * @export
 * @param {any} cameraUUID The UUID of the camera to stop livestreaming from.
 * @returns an axios promise.
 */
export function stopStreamForCameraUUID (cameraUUID) {
  /** An object used to access live video. */
  const liveVideo = owalAPIClient(LIVE_VIDEO_SERVER_URL)
  return liveVideo.delete(`/api/v1/hlsvideo/${cameraUUID}`)
}
